import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CookieConsentPreferences {
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentState {
  /** `null` until the visitor makes a choice — the banner shows while this is null. */
  preferences: CookieConsentPreferences | null;
  decidedAt: string | null;
  isPreferencesModalOpen: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  savePreferences: (preferences: CookieConsentPreferences) => void;
  openPreferencesModal: () => void;
  closePreferencesModal: () => void;
}

/**
 * Cookie consent decision. Persisted to localStorage so the choice survives
 * reloads; only `preferences`/`decidedAt` are persisted — modal visibility is
 * transient UI state and must never round-trip through storage.
 */
export const useCookieConsentStore = create<CookieConsentState>()(
  persist(
    (set) => ({
      preferences: null,
      decidedAt: null,
      isPreferencesModalOpen: false,
      acceptAll: () =>
        set({
          preferences: { analytics: true, marketing: true },
          decidedAt: new Date().toISOString(),
          isPreferencesModalOpen: false,
        }),
      rejectNonEssential: () =>
        set({
          preferences: { analytics: false, marketing: false },
          decidedAt: new Date().toISOString(),
          isPreferencesModalOpen: false,
        }),
      savePreferences: (preferences) =>
        set({ preferences, decidedAt: new Date().toISOString(), isPreferencesModalOpen: false }),
      openPreferencesModal: () => set({ isPreferencesModalOpen: true }),
      closePreferencesModal: () => set({ isPreferencesModalOpen: false }),
    }),
    {
      name: 'bfmonline.cookie-consent',
      partialize: (state) => ({ preferences: state.preferences, decidedAt: state.decidedAt }),
    },
  ),
);
